import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../../environments/environment';

/** Methods that cannot change state need no CSRF token (RFC 9110). */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Attaches the session cookie and the CSRF token to API calls.
 *
 * There is no Authorization header any more, and nothing to refresh: the API
 * holds the tokens and the browser holds an opaque, httpOnly cookie it cannot
 * read. That removes the whole token-refresh dance this interceptor used to
 * perform, and with it the storage an XSS could have raided.
 *
 * What it introduces is CSRF exposure, because cookies are sent automatically.
 * The API sets a readable `_csrf` cookie and requires it echoed in a header on
 * anything that changes state: an attacker's page can cause a request to us but
 * cannot read a cookie scoped to our origin, so it cannot supply the header.
 */
@Injectable()
export class HttpInterceptorService implements HttpInterceptor {
  private auth = inject(AuthService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(environment.api_url)) {
      return next.handle(req);
    }

    // withCredentials on every API call: without it the browser withholds the
    // cookie cross-origin and every request looks anonymous.
    let apiReq = req.clone({ withCredentials: true });

    if (!SAFE_METHODS.has(req.method.toUpperCase())) {
      const csrf = readCookie(`${environment.cookie_prefix ?? '0account'}_csrf`);
      if (csrf) {
        apiReq = apiReq.clone({ headers: apiReq.headers.set('X-CSRF-Token', csrf) });
      }
    }

    return next.handle(apiReq).pipe(
      catchError((error: unknown) => {
        // 401 means the session is gone — revoked elsewhere, or expired. There
        // is nothing to refresh, so the only honest response is to sign out.
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.auth.logout();
        }
        return throwError(() => error);
      }),
    );
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
